import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { responderLogout } from '../store/slices/responderSlice';
import { sosFetch, resetSosSession, initResponderPush } from '../utils/sosApi';
import Logo from '../components/Logo';
import '../css/sos-list.css';

const SEVERITY_COLOR = { high: '#e63946', medium: '#f4a261', low: '#2a9d8f' };
const TYPE_ICON      = { intrusion: '🚨', fire: '🔥', motion: '👁', manual_sos: '🆘', default: '⚠️' };

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)         return `${diff}s ago`;
  if (diff < 3600)       return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)      return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

function ZonePill({ zone }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '20px',
      background: 'rgba(230,57,70,0.12)',
      border: '1px solid rgba(230,57,70,0.3)',
      color: '#e63946',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
    }}>
      {zone}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const color = SEVERITY_COLOR[severity] || '#8892b0';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      background: `${color}22`,
      border: `1px solid ${color}55`,
      color,
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    }}>
      {severity}
    </span>
  );
}

function EventCard({ event, onAck }) {
  const icon    = TYPE_ICON[event.type] || TYPE_ICON.default;
  const isAcked = event.acknowledged === 1;

  return (
    <div style={{
      background: isAcked ? 'var(--surface)' : 'rgba(230,57,70,0.04)',
      border: `1px solid ${isAcked ? 'var(--border)' : 'rgba(230,57,70,0.25)'}`,
      borderLeft: `3px solid ${isAcked ? 'var(--border)' : '#e63946'}`,
      borderRadius: '8px',
      padding: '16px 20px',
      display: 'flex',
      gap: '16px',
      alignItems: 'flex-start',
      opacity: isAcked ? 0.65 : 1,
      transition: 'opacity 0.2s',
    }}>
      <div style={{ fontSize: '24px', lineHeight: 1, paddingTop: '2px', flexShrink: 0 }}>
        {icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
          <ZonePill zone={event.zone} />
          <SeverityBadge severity={event.severity} />
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {timeAgo(event.timestamp)}
          </span>
        </div>

        {event.message && (
          <div style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '6px', wordBreak: 'break-word' }}>
            {event.message}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
          <span>Type: <strong style={{ color: 'var(--text)' }}>{event.type}</strong></span>
          {event.type === 'manual_sos' && (
            <span>Raised by: <strong style={{ color: 'var(--text)' }}>{event.triggered_by || 'Unknown'}</strong></span>
          )}
          {event.source_client_id && (
            <span>Site: <strong style={{ color: 'var(--text)' }}>{event.source_client_id}</strong></span>
          )}
          {isAcked && event.acked_by && (
            <span style={{ color: '#2a9d8f' }}>✓ Acknowledged by {event.acked_by}</span>
          )}
        </div>
      </div>

      {!isAcked && (
        <button
          onClick={() => onAck(event.id)}
          style={{
            flexShrink: 0,
            padding: '6px 14px',
            background: 'transparent',
            border: '1px solid rgba(42,157,143,0.4)',
            borderRadius: '6px',
            color: '#2a9d8f',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(42,157,143,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Acknowledge
        </button>
      )}
    </div>
  );
}

export default function SosListPage() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { responderUsername, responderZones } = useSelector(s => s.responder);

  const [events,      setEvents]      = useState([]);
  const [zones,       setZones]       = useState(responderZones || []);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const timerRef = useRef(null);
  const menuRef  = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await sosFetch('/sos/events?limit=100');
      if (res.status === 401) {
        handleLogout();
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
      setZones(data.zones  || zones);
      setError('');
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to load SOS events');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchEvents();
    initResponderPush().catch(() => {});
    timerRef.current = setInterval(fetchEvents, 30_000);
    return () => clearInterval(timerRef.current);
  }, [fetchEvents]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleAck = async (eventId) => {
    try {
      await sosFetch(`/sos/events/${eventId}/ack`, { method: 'POST' });
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, acknowledged: 1, acked_by: responderUsername, acked_at: new Date().toISOString() }
        : e
      ));
    } catch {
      setError('Failed to acknowledge event');
    }
  };

  const handleLogout = () => {
    resetSosSession();
    dispatch(responderLogout());
    navigate('/responder/login', { replace: true });
  };

  const pending = events.filter(e => e.acknowledged !== 1);
  const acked   = events.filter(e => e.acknowledged === 1);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      color: 'var(--text)',
      fontFamily: 'var(--font-sans, system-ui, sans-serif)',
    }}>
      {/* ── Header ── */}
      <header className="sos-header">
        <div className="sos-brand">
          <Logo size={26} />
          <div className="sos-brand-text">
            <span className="sos-title">Parvekshak</span>
            <span className="sos-subtitle">
              <span className="sos-subtitle-full">AI Surveillance and </span>
              <span className="sos-subtitle-sos">SOS Service</span>
            </span>
          </div>
        </div>

        {pending.length > 0 && (
          <span className="sos-pending-badge">{pending.length} pending</span>
        )}

        <div className="sos-spacer" />

        {lastRefresh && (
          <span className="sos-last-refresh">Updated {timeAgo(lastRefresh)}</span>
        )}

        <div className="sos-actions">
          <button
            className={`sos-icon-btn${loading ? ' spinning' : ''}`}
            onClick={fetchEvents}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M17 10a7 7 0 1 1-2.1-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <path d="M17 4v5h-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <div className="sos-menu-wrap" ref={menuRef}>
            <button
              className="sos-icon-btn"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title="More options"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="10" cy="4"  r="1.6"/>
                <circle cx="10" cy="10" r="1.6"/>
                <circle cx="10" cy="16" r="1.6"/>
              </svg>
            </button>

            {menuOpen && (
              <div className="sos-menu" role="menu">
                <button
                  className="sos-menu-item"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); navigate('/responder/change-password'); }}
                >
                  🔑 Change Password
                </button>
                <button
                  className="sos-menu-item danger"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                >
                  ⎋ Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
        {/* ── Zone subscription summary ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          marginBottom: '24px',
          padding: '12px 16px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          fontSize: '13px',
        }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Monitoring
          </span>
          {zones.length > 0
            ? zones.map(z => <ZonePill key={z} zone={z} />)
            : <span style={{ color: 'var(--text-muted)' }}>No zones subscribed</span>
          }
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '12px' }}>
            Signed in as <strong style={{ color: 'var(--text)' }}>{responderUsername}</strong>
          </span>
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{
            padding: '10px 16px',
            background: 'rgba(231,76,60,0.1)',
            border: '1px solid rgba(231,76,60,0.25)',
            borderRadius: '6px',
            color: '#e74c3c',
            fontSize: '13px',
            marginBottom: '16px',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
            Loading SOS events…
          </div>
        )}

        {/* ── Pending events ── */}
        {!loading && (
          <>
            {pending.length === 0 && acked.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '64px 24px',
                color: 'var(--text-muted)',
                border: '1px dashed var(--border)',
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>All clear</div>
                <div style={{ fontSize: '13px' }}>No SOS events for your subscribed zones</div>
              </div>
            )}

            {pending.length > 0 && (
              <section style={{ marginBottom: '32px' }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: '#e63946',
                  marginBottom: '12px',
                }}>
                  Pending — {pending.length}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {pending.map(e => <EventCard key={e.id} event={e} onAck={handleAck} />)}
                </div>
              </section>
            )}

            {acked.length > 0 && (
              <section>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--text-muted)',
                  marginBottom: '12px',
                }}>
                  Acknowledged — {acked.length}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {acked.map(e => <EventCard key={e.id} event={e} onAck={handleAck} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
